import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import insomnia from "../../../utils/insomnia";
import style from "../../../styles/tileDownloaderStyle";
import { useNavigate } from "react-router-dom";
import { DownloadState } from "../MapDownloadPage";
import { Download, Tile, useOfflineMaps } from "../../../hooks/useOfflineMaps";
import { IndexableType, PromiseExtended } from "dexie";
import { calculateTiles } from "./MapDownloadStep3";
import _ from "lodash";

async function processTile(
  server: string,
  tile: { X: number; Y: number; Z: number },
  downloadId: number,
  addTiles: (tiles: Tile[]) => Promise<IndexableType>
) {
  const imageUrl = `https://c.tile.openstreetmap.org/${tile.Z}/${tile.X}/${tile.Y}.png`;
  const resp = await fetch(imageUrl);
  const blob = await resp.blob();
  await addTiles([{ x: tile.X, y: tile.Y, z: tile.Z, downloadId, data: blob }]);
}

async function download(
  state: DownloadState,
  databaseAccess: {
    addDownload: (download: Omit<Download, "id" | "createdAt" | "status">) => PromiseExtended<number>;
    setDownloadStatus: (id: number, status: Download["status"]) => PromiseExtended<number>;
    addTiles: (tiles: Tile[]) => PromiseExtended<IndexableType>;
  },
  progressCallback: (progress: number) => void
) {
  const tiles = calculateTiles(state);
  const downloadId = await databaseAccess.addDownload({ ...state, tileCount: tiles.length } as Download);

  let progress = 0;

  const servers = ["a", "b", "c"];

  // process in chunks so that we can have a request for each server going in parallell
  for (const chunk of _.chunk(tiles, servers.length)) {
    await Promise.all(chunk.map((tile, i) => processTile(servers[i], tile, downloadId, databaseAccess.addTiles)));
    progress += chunk.length;
    progressCallback(progress);
  }

  await databaseAccess.setDownloadStatus(downloadId, "downloaded");
}

const MapDownloadStep4: FC<{ state: DownloadState; onNext: (values: Partial<DownloadState>) => void; onBack: () => void }> = ({
  state,
}) => {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const workingRef = useRef(false);

  const { addDownload, setDownloadStatus, addTiles } = useOfflineMaps();

  const startNewDownload = useCallback(() => {
    if (workingRef.current) {
      return;
    }
    workingRef.current = true;
    setError(null);

    insomnia.start();
    download(state, { addDownload, setDownloadStatus, addTiles }, setProgress)
      .then(() => setCompleted(true))
      .catch(setError)
      .finally(() => {
        insomnia.stop();
        workingRef.current = false;
      });
  }, [addDownload, addTiles, setDownloadStatus, state]);

  useEffect(() => startNewDownload(), [startNewDownload]);

  const tiles = calculateTiles(state);
  const progressPercent = Math.round((progress / tiles.length) * 100);

  const navigate = useNavigate();
  const buttonCancelClick = useCallback(() => {
    navigate("../");
  }, [navigate]);

  const buttonRetryClick = useCallback(() => {
    startNewDownload();
  }, [startNewDownload]);

  const css = style;

  return (
    <div>
      <div className="progress">
        <div
          className="progress-bar progress-bar-info"
          role="progressbar"
          style={{ width: progressPercent > 7 ? progressPercent + "%" : "7%" }}
        >
          {progressPercent + "%"}
        </div>
      </div>

      <button onClick={buttonCancelClick} className="button  button-fill" style={css.buttonConfirm}>
        {completed ? "Close" : "Cancel"}
      </button>
      {error ? <p style={{ color: "red" }}>Error: {error}</p> : null}
      {error ? (
        <button onClick={buttonRetryClick} className="button  button-fill" style={css.buttonConfirm}>
          Retry
        </button>
      ) : null}
    </div>
  );
};

export default MapDownloadStep4;
